import { Icon } from "@phosphor-icons/react";
import { Input, Table as VoidTable, TableColumn } from "@void-orbit/ui";

import { Color } from "../Buttons";
import { UserRole } from "../../models/User";
import { useStore } from "../../store";
import { getAssignedRole, hasRole } from "../../services/accessControl";

export interface Action<TypeData> {
  icon: Icon;
  label: string;
  onClick: ( value: TypeData ) => void;
  color: Color;
  hidden?: ( value: TypeData ) => boolean;
};

export interface Header<TypeData> {
  label: string;
  key: keyof TypeData;
  transform?: ( value: any ) => string;
  nested?: ( row: TypeData ) => string;
  component?: ( row: TypeData ) => React.ReactNode;
  align?: "left" | "center" | "right";
  maxPossibleWidth?: boolean;
  roles?: ReadonlyArray<UserRole>;
};

export interface TableSearchProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: ( value: string ) => void;
};

export interface TableProps<TypeData> {
  keyProperty: keyof TypeData;
  headers: Array<Header<TypeData>>;
  actions?: Array<Action<TypeData>>;
  data: Array<TypeData>;
  page: number;
  perPage: number;
  total: number;
  loading?: boolean;
  changePage: ( page: number ) => void;
  onClickRow?: ( value: TypeData ) => void;
  search?: TableSearchProps;
  filters?: React.ReactNode;
};

const toColumn = <TypeData,>({ label, key, transform, component, nested, align, maxPossibleWidth }: Header<TypeData> ): TableColumn<TypeData> => ({
  id: `${ String( key )}_${ label }`,
  header: label,
  align,
  maxPossibleWidth,
  cell: ( row ) => transform
    ? transform( row[ key ] ?? "" )
    : component
      ? component( row )
      : nested
        ? nested( row )
        : String( row[ key ] ?? "" ),
});

export const Table = <TypeData,>({
  keyProperty,
  headers,
  actions,
  data,
  page,
  perPage,
  total,
  loading = false,
  changePage,
  onClickRow,
  search,
  filters,
}: TableProps<TypeData> ) => {
  const {
    user,
  } = useStore();

  const assignedRole = getAssignedRole( user );
  const visibleHeaders = headers.filter(( header ) => ( header.roles ?? []).length > 0 ? hasRole( assignedRole, header.roles ) : true );

  return (
    <VoidTable<TypeData>
      rows={ data }
      columns={ visibleHeaders.map( toColumn ) }
      getRowId={( row ) => String( row[ keyProperty ])}
      loading={ loading }
      onRowClick={ onClickRow }
      actions={ actions?.map(({ icon: ActionIcon, label, onClick, color, hidden }) => ({
        label,
        onClick,
        color,
        hidden,
        icon: <ActionIcon size={ 16 } weight="bold" />,
      })) }
      pagination={{ page, pageSize: perPage, total, onPageChange: changePage }}
      toolbar={ search || filters
        ? (
            <>
              {
                search && (
                  <Input
                    id="table-search"
                    showMessage={ false }
                    label={ search.label ?? "Buscar" }
                    value={ search.value }
                    placeholder={ search.placeholder ?? "Digite para buscar" }
                    onChange={({ target: { value }}) => search.onChange( value )}
                  />
                )
              }

              { filters }
            </>
          )
        : undefined }
    />
  );
};
